import type {
  ClientOrganizationInGeoArea,
  GetOrganizationsInOwnGeoAreaSelectionTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { AnimatedLegendList } from '@legendapp/list/reanimated';
import { useScrollToTop } from '@react-navigation/native';
import type { TFunction } from 'i18next';
import { useMemo, useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Text } from 'react-native-paper';

import { useGetOrganizationsInOwnGeoAreaSelection } from '@/features/api/hooks/grapevine-organization/useGetOrganizationsInOwnGeoAreaSelection';
import { GeoAreaRoute } from '@/features/geo-area/navigation/geoAreaRoute';
import { ListItem } from '@/features/list/components/ListItem';
import { organizationLogoIcon } from '@/features/media/components/avatar/OrganizationLogo';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type MeinOrtTFunction = TFunction<'my-place', 'screens.myPlace'>;

type OrganizationEmptyKey =
  | 'organizations.empty.vereine'
  | 'organizations.empty.ehrenamt'
  | 'organizations.empty.schulen'
  | 'organizations.empty.kirche';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    list: { flex: 1 },
    content: {
      flexGrow: 1
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 32
    },
    card: {
      marginHorizontal: 12,
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    empty: { marginHorizontal: 16, marginTop: 8 }
  });

const memberCountLabel = (t: MeinOrtTFunction, count?: number) =>
  t('organizations.members', { count: count ?? 0 });

export const MeinOrtOrganizationsTab: FC<{
  tag: GetOrganizationsInOwnGeoAreaSelectionTagsEnum;
  emptyKey: OrganizationEmptyKey;
}> = ({ tag, emptyKey }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const navigation = useAppNavigation();
  const { t } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace'
  });
  const { scrollY, feedInset } = useMeinOrtScroll();

  const tags = useMemo(() => [tag], [tag]);

  const {
    data: organizations,
    refetch,
    isPending,
    isRefetching,
    isError
  } = useGetOrganizationsInOwnGeoAreaSelection({ tags });

  const listRef = useRef(null);
  useScrollToTop(listRef);

  const sharedValues = useMemo(() => ({ scrollOffset: scrollY }), [scrollY]);

  const renderItem = ({ item }: { item: ClientOrganizationInGeoArea }) => (
    <ListItem
      title={item.name}
      description={memberCountLabel(t, item.memberCount)}
      icon={organizationLogoIcon(item.logoImage)}
      backgroundColor={theme.colors.surfaceContainerLowest}
      onPress={() =>
        navigation.navigate(GeoAreaRoute.OrganizationProfile, { id: item.id })
      }
      hideDivider
    />
  );

  if (isPending) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <ActivityIndicator size={48} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <Text variant="titleMedium">{t('state.error.title')}</Text>
        <Text variant="bodyMedium">{t('state.error.subtitle')}</Text>
        <Button mode="contained-tonal" onPress={() => refetch()}>
          {t('state.error.retry')}
        </Button>
      </View>
    );
  }

  if (!hasItems(organizations)) {
    return (
      <View style={[styles.content, { paddingTop: feedInset }]}>
        <EmptyCard style={styles.empty} description={t(emptyKey)} />
      </View>
    );
  }

  return (
    <AnimatedLegendList
      ref={listRef}
      style={styles.list}
      data={organizations}
      refreshControl={
        <Refresher refreshing={isRefetching} onRefresh={refetch} />
      }
      renderItem={renderItem}
      recycleItems
      sharedValues={sharedValues}
      keyExtractor={(item) => item.id}
      contentContainerStyle={[styles.content, { paddingTop: feedInset }]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    />
  );
};
